/**
 * MODULE: _shared/frontend-shell/src/components/NotFoundOrAuthRedirect.tsx
 * PURPOSE: Redirect unauthenticated users to /auth when they hit the wildcard (*) route instead of showing 404.
 *
 * DEPENDENCIES:
 *   - react, react-router-dom (Navigate)
 *   - @core/auth (useAuth)
 *   - ./LoadingSpinner
 *
 * PUBLIC API:
 *   - NotFoundOrAuthRedirect({ children }) - Renders children (NotFound) when authenticated; redirects to /auth when not.
 *
 * SIDE EFFECTS:
 *   - Navigation redirect to /auth when not authenticated
 *
 * AGENT_SAFETY: 🟡 RESTRICTED - Affects app-wide 404 behavior and auth flow
 * FILE_SIZE: ~35 / 150 lines
 *
 * Recent Changes:
 *   - 2026-02-08: Added; wraps NOT_FOUND route so direct hits to protected paths (e.g. /dashboard) redirect to login.
 */
import { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@core/auth';
import { LoadingSpinner } from './LoadingSpinner';

interface NotFoundOrAuthRedirectProps {
  children: ReactNode;
}

export function NotFoundOrAuthRedirect({ children }: NotFoundOrAuthRedirectProps) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingSpinner fullScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth" replace />;
  }

  return <>{children}</>;
}
